import type {
  ParsedWordSegment,
  EmbeddingLevelsResult,
  TextDirection,
} from '../types';
import { isRTLLevel } from '../utils/level-utils';

/**
 * Phase 3: Word Directionality Analysis
 *
 * Determine direction (ltr/rtl/neutral) for each word based on embedding levels.
 * Assign embedding level to each word based on most dominant directionality.
 */
export const getWordDirectionality = (
  word: ParsedWordSegment,
  embeddingLevels: EmbeddingLevelsResult,
  startIndex: number,
): TextDirection | 'neutral' => {
  if (!word.isWordLike) {
    return 'neutral';
  }

  const wordLevels = embeddingLevels.levels.slice(
    startIndex,
    startIndex + word.data.length,
  );

  // Count RTL and LTR characters based on embedding levels
  const rtlChars = wordLevels.filter(isRTLLevel).length;
  const ltrChars = wordLevels.length - rtlChars;

  if (rtlChars > ltrChars) {
    return 'rtl';
  }
  if (ltrChars > rtlChars) {
    return 'ltr';
  }

  // If equal, use the first strong directional character
  const firstStrongLevel = wordLevels.find(level => level > 0);
  if (firstStrongLevel !== undefined) {
    return isRTLLevel(firstStrongLevel) ? 'rtl' : 'ltr';
  }

  return 'neutral';
};
